import { useCallback, useEffect, useRef, useState } from "react";
import PdfReportApi from "src/services/apis/pdfReport";
import { PROCTORING_TYPES } from "src/constants/globalConstants";
import { AI_PROCTORING } from "../constant";

/*
 * What the report PAGE needs from proctoring, taken over from the old
 * ProctoringModal (which fetched everything on page load):
 *  - the candidate's user (camera) session list, fetched once on load: the
 *    header's Proctoring button and its "Camera not working" hint read its length;
 *  - the snapshots the full PDF prints, fetched only when a PDF asks for them
 *    (ReportDownloadComponent calls fetchSnapshotsForReportDownload), in the
 *    same shape and order the page returned before.
 *
 * Nothing else is fetched on page load; the review room loads its own data.
 * `criticalReason` is kept as the snapshot's own data (the modal replaced it
 * with tooltip JSX, which the PDF never read).
 */

/** A snapshot flagged when it was taken, whatever the AI said later. */
export const hasSnapshotCriticalReason = (snap) => {
  if (typeof snap?.criticalReason === "string") {
    return snap.criticalReason.trim().length > 0;
  }
  return !!snap?.criticalReason;
};

const getSnapshotTimeInSeconds = (snap, fallbackSeconds) => {
  const timestamp = Number(snap?.timestamp);
  if (!Number.isNaN(timestamp)) return timestamp;

  const createdAt = new Date(snap?.createdAt).getTime();
  if (!Number.isNaN(createdAt)) return Math.floor(createdAt / 1000);

  return fallbackSeconds;
};

/**
 * When the image AI was skipped (cutoff failed), the snapshots it would have
 * looked at: one per interval from the first, plus every one flagged when taken.
 */
export const filterSkippedAiSnapshots = (snapshots, intervalInSeconds = 30) => {
  let intervalAnchor = null;

  return snapshots.filter((snap, index) => {
    const snapshotTime = getSnapshotTimeInSeconds(snap, index * intervalInSeconds);
    if (intervalAnchor === null) intervalAnchor = snapshotTime;

    if (hasSnapshotCriticalReason(snap)) {
      return true;
    }

    if (index === 0 && !snap?.aiProctoringData) return false;

    return (snapshotTime - intervalAnchor) % intervalInSeconds === 0;
  });
};

const isAiFlagged = (snap) => [AI_PROCTORING.TYPES.CHEATING, AI_PROCTORING.TYPES.MAYBECHEATING].includes(snap?.aiProctoringData?.status);

/**
 * The link's image-proctoring snapshots (GET /api/proctoring/v1?linkId), as the
 * modal prepared them: oldest first; with aiProctoring, only the analysed ones
 * (or the skipped-AI selection), each marked critical or not.
 */
export const prepareLinkSnapshots = (data, positionConfig, cutOffCleared) => {
  const sorted = (data || []).slice().sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
  if (!positionConfig?.aiProctoring) {
    /* as before: no `critical` flag, so every one prints as a plain snapshot */
    return sorted.map((snap) => ({ ...snap, criticalType: AI_PROCTORING.TYPES.CHEATING }));
  }
  const showAllSnapshots = positionConfig.skipAiProcOnCutoffFail && cutOffCleared === false;
  const aiProctoringInterval = Number(positionConfig?.aiProctoringInterval) || 30;
  const kept = showAllSnapshots
    ? filterSkippedAiSnapshots(sorted, aiProctoringInterval)
    : sorted.filter((snap) => !!snap?.aiProctoringData);
  return kept.map((snap) => {
    const isCritical = isAiFlagged(snap);
    const hasSkippedAiCriticalReason = !snap?.aiProctoringData && hasSnapshotCriticalReason(snap);
    return {
      ...snap,
      critical: isCritical || hasSkippedAiCriticalReason,
      criticalType: hasSkippedAiCriticalReason ? AI_PROCTORING.TYPES.CHEATING : snap?.aiProctoringData?.status,
      hasReadinessTooltip: !!(positionConfig.trustScorePersonalityAnalysis && snap?.aiProctoringData?.professionalReadiness),
    };
  });
};

/**
 * One video session's snapshots (video-stream GET /api/snapshot/v1?sessionId), as
 * the page prepared them for the PDF.
 */
export const prepareSessionSnapshots = (data, positionConfig, cutOffCleared) => {
  if (positionConfig.screenProctoring || positionConfig.aiProctoring) {
    const showAllSnapshots = positionConfig.skipAiProcOnCutoffFail && cutOffCleared === false;
    const aiProctoringInterval = Number(positionConfig?.aiProctoringInterval) || 30;
    return (showAllSnapshots ? filterSkippedAiSnapshots(data, aiProctoringInterval) : data.filter((snap) => !!snap?.aiProctoringData))
      .map((snap) => {
        const hasSkippedAiCriticalReason = !snap?.aiProctoringData && hasSnapshotCriticalReason(snap);
        return {
          ...snap,
          critical: isAiFlagged(snap) || hasSkippedAiCriticalReason,
          criticalType: hasSkippedAiCriticalReason ? AI_PROCTORING.TYPES.CHEATING : snap?.aiProctoringData?.status,
        };
      });
  }
  return data.map((snap) => ({
    ...snap,
    critical: snap?.criticalReason?.length > 0,
    criticalType: AI_PROCTORING.TYPES.CHEATING,
  }));
};

/**
 * @param {Object} args
 * @param {string} args.linkId
 * @param {Object|null} args.linkConfig
 * @param {Object|null} args.positionConfig
 * @param {Object|null} args.reportData
 */
export default function useProctoringSummary({ linkId, linkConfig, positionConfig, reportData }) {
  const [userVideoSessionsList, setUserVideoSessionsList] = useState([]);
  const [referenceImageLink, setReferenceImageLink] = useState(null);

  /* the latest values for the PDF's call, which can come at any time */
  const latest = useRef({});
  latest.current = { linkId, linkConfig, positionConfig, reportData, userVideoSessionsList, referenceImageLink };

  /* the one proctoring fetch on page load: the camera sessions, for the header */
  useEffect(() => {
    if (!linkId) return undefined;
    let live = true;
    (async () => {
      try {
        const response = await PdfReportApi.getVideoSessions(linkId, PROCTORING_TYPES.VIDEO);
        if (live && response?.success) setUserVideoSessionsList(response.data || []);
      } catch (err) {
        console.error(err);
      }
    })();
    return () => { live = false; };
  }, [linkId]);

  const fetchSessionSnapshots = useCallback(async (sessionId) => {
    const { positionConfig: P, reportData: R } = latest.current;
    try {
      const response = await PdfReportApi.getSessionSnapshots(sessionId);
      if (!response?.success) return undefined;
      if (response.referenceImageLink) setReferenceImageLink(response.referenceImageLink);
      /* before, a missing position config threw here and the session printed no snapshots */
      if (!P) return { snapshots: [], referenceImageLink: null };
      return {
        snapshots: prepareSessionSnapshots(response.data, P, R?.cutOffCleared),
        referenceImageLink: response.referenceImageLink,
      };
    } catch (err) {
      return { snapshots: [], referenceImageLink: null };
    }
  }, []);

  /**
   * The PDF's proctoring pages: [nonCritical, critical, referenceImageLink?].
   * Loads on call; the page fetched none of it before a PDF asked.
   */
  const fetchSnapshotsForReportDownload = useCallback(async () => {
    const { linkId: L, linkConfig: LC, positionConfig: P, reportData: R, userVideoSessionsList: sessions, referenceImageLink: ref } = latest.current;
    try {
      if (LC?.proctoring || P?.proctoring) {
        /* image proctoring. As before, only a link with image proctoring on has
           snapshots to print; a position-only setting printed none. */
        if (!LC?.proctoring) return [[], []];
        const response = await PdfReportApi.fetchSnapshots(L);
        const all = response?.success ? prepareLinkSnapshots(response.data, P, R?.cutOffCleared) : [];
        return [all.filter((snap) => !snap.critical), all.filter((snap) => snap.critical)];
      }
      let response = await Promise.allSettled((sessions || []).map((session) => fetchSessionSnapshots(session.id)));
      response = response.filter((result) => result.status === "fulfilled" && result.value?.snapshots).map((result) => result.value);
      const allVideoSnapshots = [];
      let fetchedReferenceImageLink = ref;
      response.forEach(({ snapshots, referenceImageLink: sessionReferenceImageLink }) => {
        allVideoSnapshots.push(...snapshots);
        if (!fetchedReferenceImageLink && sessionReferenceImageLink) {
          fetchedReferenceImageLink = sessionReferenceImageLink;
        }
      });
      return [allVideoSnapshots.filter((snap) => !snap.critical), allVideoSnapshots.filter((snap) => snap.critical), fetchedReferenceImageLink];
    } catch (err) {
      console.error(err);
      return [[], [], ref];
    }
  }, [fetchSessionSnapshots]);

  return { userVideoSessionsList, referenceImageLink, fetchSnapshotsForReportDownload };
}
